import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  FloatingSave,
  MultiSelect,
  Page,
  SimpleSelect,
  SwitchInput,
  TextInput,
  UrlInput,
} from 'corso-admin-ui';
import {
  ClaimReasonEnum,
  FulfillmentStrategyEnum,
  fulfillmentStrategyName,
  isTruthy,
  shippingClaimReasonName,
} from 'corso-types';
import { FormEventHandler } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ClipboardButton from '~/components/ClipboardButton';
import {
  useConfigSettings,
  useConfigSettingsUpdate,
} from '~/hooks/useConfigSettings';
import useIsCorsoAdmin from '~/hooks/useIsCorsoAdmin';
import { useThemeSettings } from '~/hooks/useThemeSettings';
import { shipProtectSettingsFormSchema } from '~/types';
import { ShippingClaimRules } from './ShippingClaimRules';

function ShippingProtectionForm() {
  const { mutateAsync: saveChanges } = useConfigSettingsUpdate();

  const { data: themeSettings } = useThemeSettings();

  const isCorsoAdmin = useIsCorsoAdmin();

  const { data } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );

  const allReasonOptions = Object.values(ClaimReasonEnum).map((reason) => ({
    value: reason,
    label: shippingClaimReasonName[reason],
  }));

  const fulfillmentStrategyOptions = Object.values(FulfillmentStrategyEnum).map(
    (strategy) => ({
      value: strategy,
      label: fulfillmentStrategyName[strategy],
    }),
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(shipProtectSettingsFormSchema),
    values: data,
    defaultValues: {
      offeredClaimReasons: [],
    },
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) =>
      // RHF drops disabled (admin-only) fields from values, so keep their saved state
      saveChanges({ shippingProtection: { ...data, ...values } }),
    )(event).catch(console.error);
  };

  const reorderTemplateUrl = themeSettings?.customerAppUrl
    ? `${themeSettings.customerAppUrl}/reorder/{{order.id}}`
    : '';
  const formId = 'shipping-protection-settings';

  return (
    <form
      className="flex flex-col gap-4"
      id={formId}
      onSubmit={submitHandler}
      onReset={(e) => {
        e.preventDefault();
        reset();
      }}
    >
      <FloatingSave
        isDirty={isDirty}
        isSubmitting={isSubmitting}
        form={formId}
      />

      <Card>
        <UrlInput
          id="customer-portal"
          label="Reorder Template URL"
          details="Use this URL inside of Shopify liquid templates to direct users to the Reorder portal, this is typically used in order confirmation emails when a customer purchases Shipping Plus."
          value={reorderTemplateUrl}
          required
          readOnly
          disabled
          addon={{
            outsideEnd: (
              // using `grid` on mobile and `flex` on desktop avoid apply `grow` to each child
              <div className="grid grid-cols-2 items-center gap-2 md:flex">
                <ClipboardButton onClick={() => reorderTemplateUrl} />
              </div>
            ),
          }}
        />

        <Controller
          control={control}
          name="isClaimImageFromCustomerRequired"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="claim-image-required"
              label="Require Images for Damaged Claims"
              details="When enabled, customers are required to upload an image when submitting a claim for a damaged item."
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="shouldSendPurchaseConfirmation"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="should-send-purchase-confirmation"
              label="Send Corso Shipping Purchase Confirmation"
              details="When enabled, customers will receive an email confirmation from Corso after purchasing Shipping Plus."
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="giftCardProductIdFromPlatform"
          render={({ fieldState, field: { value, onChange } }) => (
            <TextInput
              id="gift-card-product-id-from-platform"
              label="Store Credit Gift Card Product ID"
              details="The product ID for the Corso team to use when generating gift cards for store credit."
              error={fieldState.error?.message}
              onChange={onChange}
              value={value}
            />
          )}
        />

        <Controller
          control={control}
          name="shipProtectOrderTag"
          render={({ fieldState, field: { value, onChange } }) => (
            <TextInput
              id="ship-protect-order-tag"
              label="Protected Order Tag"
              details="The tag to apply to protected orders, this applies to both Shipping Plus and product based protection."
              error={fieldState.error?.message}
              onChange={onChange}
              value={value}
            />
          )}
        />

        <Controller
          control={control}
          disabled={!isCorsoAdmin}
          name="isZeroDollarReorderEnabled"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="is-zero-dollar-reorder-enabled"
              label="No Cost Reorders"
              details="When enabled, Corso will issue reorders for $0, using the configured No Cost Reorder Discount Title."
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="zeroDollarReorderDiscountTitle"
          render={({ fieldState, field: { value, onChange } }) => (
            <TextInput
              id="zero-dollar-reorder-discount-title"
              label="No Cost Reorder Discount Title"
              details="The discount title to use for no cost reorders. "
              error={fieldState.error?.message}
              onChange={onChange}
              value={value}
            />
          )}
        />

        <Controller
          disabled={!isCorsoAdmin}
          control={control}
          name="isRefundMultipleGspEnabled"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              details="When enabled, orders that contain multiple protection products will be adjusted to contain the correct quantity."
              id="is-refund-multiple-gsp-enabled"
              label="Refund Multiple GSP"
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          disabled={!isCorsoAdmin}
          name="offeredClaimReasons"
          render={({ field, fieldState }) => (
            <MultiSelect
              label="Shipping Claim Reasons"
              options={allReasonOptions}
              details="Select the claim reasons that customers can choose from when filing a shipping claim."
              value={field.value
                .map((value) =>
                  allReasonOptions.find((option) => option.value === value),
                )
                .filter(isTruthy)}
              // map selected option objects back to the raw values for the form state
              onChange={(selected) =>
                field.onChange(selected.map(({ value }) => value))
              }
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          disabled={!isCorsoAdmin}
          name="fulfillmentStrategy"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Fulfillment Strategy"
              details="The point in time Corso will attempt to fulfill protection products associated with an order."
              value={field.value}
              options={fulfillmentStrategyOptions}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </Card>
    </form>
  );
}

export default function ShippingProtectionSettingsPage() {
  const isCorsoAdmin = useIsCorsoAdmin();
  return (
    <Page title="Shipping Claims">
      <div className="flex flex-col gap-4">
        <ShippingProtectionForm />
        {isCorsoAdmin && <ShippingClaimRules />}
      </div>
    </Page>
  );
}
